/* eslint-disable */
import { useState, useEffect } from "react";
import { format, addDays, subDays, isToday, parse, isBefore } from "date-fns";
import { motion, Reorder, useMotionValue, useTransform } from "framer-motion";
import { CheckCircle, Circle, ChevronLeft, ChevronRight, Edit, Save, Plus, Trash, Clock, X } from "lucide-react";
import { db } from "../environments/firebase";
import { ref, set, get, update } from "firebase/database";
import { useAuth } from "../contexts/AuthContext";

interface Task {
  id: string;
  text: string;
  scheduledTime?: string; // Format: "HH:mm" (24-hour)
  completedAt?: string;
}

interface Message {
  id: string;
  content: string;
  position: 'top-right' | 'bottom';
}

export default function DailyTasks() {
  const { currentUser } = useAuth();
  const [date, setDate] = useState(new Date());
  const [completed, setCompleted] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [userTasks, setUserTasks] = useState<Task[]>([]);
  const [newTaskText, setNewTaskText] = useState("");
  const [isAddingTime, setIsAddingTime] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [timeInput, setTimeInput] = useState("");
  const [visitedBefore, setVisitedBefore] = useState<boolean>(false);
  const [currentMessage, setCurrentMessage] = useState<Message | null>(null);
  const [messageQueue, setMessageQueue] = useState<Message[]>([]);
  const [highlightEditIcon, setHighlightEditIcon] = useState(false);

  const defaultTasks: Task[] = [
    { id: "1", text: "CLick on Edit icon to configure" },
    { id: "2", text: "Morning Aarti" },
    { id: "3", text: "Vachnamrut/Swamini Vat Vanchan" },
    { id: "4", text: "Jivan Charitra Vanchan" },
    { id: "5", text: "AntarDrashti" },
    { id: "6", text: "Guruhari Darshan" },
    { id: "7", text: "Prapti Vichar - 15 min" },
    { id: "8", text: "Brahmna Sange" },
    { id: "9", text: "Mansi --- 3" },
    { id: "10", text: "Mansi --- 4" },
    { id: "11", text: "Mansi --- 5" },
  ];

  // Check if task is in the past
  const isPastTask = (task: Task) => {
    if (!task.scheduledTime || !isToday(date)) return false;
    
    const now = new Date();
    const [hours, minutes] = task.scheduledTime.split(':').map(Number);
    const taskTime = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hours,
      minutes
    );
    
    return isBefore(taskTime, now);
  };

  // Initialize messages and check if user has visited before
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisitedDailyTasks');
    if (hasVisited) {
      setVisitedBefore(true);
    } else {
      // Set up messages to show
      const messages: Message[] = [
        {
          id: '1',
          content: 'Welcome! Click the edit button to customize your daily spiritual practice tasks.',
          position: 'top-right'
        },
        {
          id: '2',
          content: 'Track your progress here. Complete tasks by clicking the circles.',
          position: 'bottom'
        }
      ];
      
      setMessageQueue(messages);
      setCurrentMessage(messages[0]);
      localStorage.setItem('hasVisitedDailyTasks', 'true');
      console.log('First time user visited Daily Tasks');
      
      // Highlight the edit icon for first-time users
      setHighlightEditIcon(true);
      const timer = setTimeout(() => {
        setHighlightEditIcon(false);
      }, 5000); // Stop highlighting after 5 seconds
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle message queue
  const handleNextMessage = () => {
    if (messageQueue.length > 0) {
      const newQueue = [...messageQueue];
      newQueue.shift(); // Remove current message
      setMessageQueue(newQueue);
      
      if (newQueue.length > 0) {
        setCurrentMessage(newQueue[0]);
      } else {
        setCurrentMessage(null);
      }
    } else {
      setCurrentMessage(null);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        
        const userTemplateRef = ref(db, `users/${currentUser.uid}/taskTemplate`);
        const templateSnapshot = await get(userTemplateRef);
        
        if (templateSnapshot.exists()) {
          setUserTasks(templateSnapshot.val());
        } else {
          await set(userTemplateRef, defaultTasks);
          setUserTasks(defaultTasks);
        }
        
        const taskDetailsRef = ref(db, `users/${currentUser.uid}/taskDetails`);
        const snapshot = await get(taskDetailsRef);
        const taskDetails = snapshot.val() || {};

        const today = format(date, "yyyy-MM-dd");
        const completedToday = taskDetails[today] || [];

        const completedTaskIds = userTasks
          .filter((task) =>
            completedToday.some((completedTask: any) => completedTask.task === task.text)
          )
          .map((task) => task.id);
        setCompleted(completedTaskIds);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, date]);

  const toggleTask = async (id: string, taskName: string) => {
    if (!currentUser) return;

    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    const newCompleted = completed.includes(id)
      ? completed.filter((x) => x !== id)
      : [...completed, id];

    setCompleted(newCompleted);

    // Update task with completion time
    const updatedTasks = userTasks.map(task => {
      if (task.id === id) {
        return newCompleted.includes(id)
          ? { ...task, completedAt: currentTime }
          : { ...task, completedAt: undefined };
      }
      return task;
    });
    setUserTasks(updatedTasks);

    await updateTaskCompletionInFirebase(taskName, currentTime, newCompleted);
  };

  const updateTaskCompletionInFirebase = async (
    taskName: string,
    time: string,
    newCompleted: string[]
  ) => {
    if (!currentUser) return;

    const taskDetailsRef = ref(db, `users/${currentUser.uid}/taskDetails`);
    const today = format(date, "yyyy-MM-dd");

    try {
      const snapshot = await get(taskDetailsRef);
      const taskDetails = snapshot.val() || {};

      const tasksForToday = taskDetails[today] || [];
      const taskExists = tasksForToday.some((task: any) => task.task === taskName);

      const taskId = userTasks.find((task) => task.text === taskName)?.id;

      if (taskId !== undefined && !taskExists && newCompleted.includes(taskId)) {
        const updatedTaskDetails = {
          ...taskDetails,
          [today]: [...tasksForToday, { task: taskName, completedAt: time }],
        };
        await set(taskDetailsRef, updatedTaskDetails);
      } else if (taskId !== undefined && !newCompleted.includes(taskId)) {
        const updatedTaskDetails = {
          ...taskDetails,
          [today]: tasksForToday.filter((task: any) => task.task !== taskName),
        };
        await set(taskDetailsRef, updatedTaskDetails);
      }
    } catch (error) {
      console.error("Error updating task completion:", error);
    }
  };

  const handleDateChange = (newDate: Date) => {
    setDate(newDate);
  };

  const saveTaskTemplate = async () => {
    if (!currentUser) return;
    
    try {
      const userTemplateRef = ref(db, `users/${currentUser.uid}/taskTemplate`);
      await set(userTemplateRef, userTasks);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving task template:", error);
    }
  };

  const addNewTask = () => {
    if (!newTaskText.trim()) return;
    
    const newTask: Task = {
      id: Date.now().toString(),
      text: newTaskText.trim()
    };
    
    setUserTasks([...userTasks, newTask]);
    setNewTaskText("");
  };

  const removeTask = (id: string) => {
    setUserTasks(userTasks.filter(task => task.id !== id));
  };

  const promptForTime = (taskId: string) => {
    setCurrentTaskId(taskId);
    setIsAddingTime(true);
    const task = userTasks.find(t => t.id === taskId);
    setTimeInput(task?.scheduledTime || "");
  };

  const saveTimeForTask = () => {
    if (!currentTaskId) return;
    
    const updatedTasks = userTasks.map(task => {
      if (task.id === currentTaskId) {
        return {
          ...task,
          scheduledTime: timeInput || undefined
        };
      }
      return task;
    });
    
    setUserTasks(updatedTasks);
    setIsAddingTime(false);
    setCurrentTaskId(null);
    setTimeInput("");
  };

  const formatTimeDisplay = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Message Box Component
  const MessageBox = ({ message }: { message: Message }) => {
    return (
      <div className={`fixed ${message.position === 'top-right' ? 'top-4 right-4' : 'bottom-4 left-1/2 transform -translate-x-1/2'} 
        z-50 bg-white p-4 rounded-xl shadow-xl border border-indigo-200 max-w-xs md:max-w-sm`}>
        <div className="flex justify-between items-start mb-2">
          <div className="w-4 h-4 rounded-full bg-indigo-500 flex-shrink-0 mt-1"></div>
          <p className="flex-1 mx-2 text-sm text-gray-700">{message.content}</p>
          <button 
            onClick={handleNextMessage}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleNextMessage}
            className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Okay
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 p-4 md:p-8 ">
      {/* Message Boxes */}
      {currentMessage && <MessageBox message={currentMessage} />}

      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-600 p-6 text-white relative">
          <h1 className="text-2xl font-bold text-center">Nitya Ahnik </h1>
          {!isEditing && (
            <motion.button
              onClick={() => setIsEditing(true)}
              className="absolute right-4 top-4 p-2 rounded-full hover:bg-indigo-700 transition-colors"
              aria-label="Edit tasks"
              animate={{
                scale: highlightEditIcon ? [1, 1.2, 1] : 1,
                backgroundColor: highlightEditIcon ? "rgba(255, 255, 255, 0.2)" : "transparent"
              }}
              transition={{
                scale: highlightEditIcon ? {
                  repeat: Infinity,
                  repeatType: "reverse",
                  duration: 1.5
                } : {},
                backgroundColor: { duration: 0.3 }
              }}
            >
              <Edit className="w-5 h-5" />
            </motion.button>
          )}
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between p-4 bg-indigo-50">
          <button
            onClick={() => handleDateChange(subDays(date, 1))}
            className="p-2 rounded-full hover:bg-indigo-100 transition-colors"
            aria-label="Previous day"
          >
            <ChevronLeft className="w-6 h-6 text-indigo-700" />
          </button>
          
          <div className="text-center">
            <h2 className="text-lg font-semibold text-indigo-800">
              {format(date, "EEEE, MMMM d")}
            </h2>
            <p className="text-sm text-indigo-600">
              {isToday(date) ? "Today" : format(date, "yyyy")}
            </p>
          </div>
          
          <button
            onClick={() => handleDateChange(addDays(date, 1))}
            className="p-2 rounded-full hover:bg-indigo-100 transition-colors"
            aria-label="Next day"
            disabled={isToday(date)}
          >
            <ChevronRight className={`w-6 h-6 ${isToday(date) ? 'text-indigo-300' : 'text-indigo-700'}`} />
          </button>
        </div>

        {/* Time Input Modal */}
        {isAddingTime && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Set Time</h3>
                <button 
                  onClick={() => setIsAddingTime(false)} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Time (24-hour format)
                </label>
                <input
                  type="time"
                  value={timeInput}
                  onChange={(e) => setTimeInput(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsAddingTime(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTimeForTask}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Save Time
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Task List */}
        <div className="p-4 space-y-3">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : isEditing ? (
            <>
              <div className="flex mb-4">
                <input
                  type="text"
                  value={newTaskText}
                  onChange={(e) => setNewTaskText(e.target.value)}
                  placeholder="Add new task"
                  className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && addNewTask()}
                />
                <button
                  onClick={addNewTask}
                  className="bg-indigo-600 text-white px-4 rounded-r-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              <Reorder.Group 
                axis="y" 
                values={userTasks} 
                onReorder={setUserTasks} 
                className="space-y-3"
              >
                {userTasks.map((task) => (
                  <Reorder.Item 
                    key={task.id} 
                    value={task}
                    drag
                    className="relative flex items-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                    whileDrag={{
                      scale: 1.03,
                      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                      zIndex: 10
                    }}
                  >
                    <div className="mr-3 cursor-grab touch-none">
                      <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="2" cy="2" r="2" fill="#D1D5DB"/>
                        <circle cx="2" cy="10" r="2" fill="#D1D5DB"/>
                        <circle cx="2" cy="18" r="2" fill="#D1D5DB"/>
                        <circle cx="10" cy="2" r="2" fill="#D1D5DB"/>
                        <circle cx="10" cy="10" r="2" fill="#D1D5DB"/>
                        <circle cx="10" cy="18" r="2" fill="#D1D5DB"/>
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={task.text}
                      onChange={(e) => {
                        const updatedTasks = userTasks.map(t => 
                          t.id === task.id ? {...t, text: e.target.value} : t
                        );
                        setUserTasks(updatedTasks);
                      }}
                      className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    <div className="flex ml-2 space-x-1">
                      <button
                        onClick={() => promptForTime(task.id)}
                        className="p-2 text-indigo-500 hover:text-indigo-700 transition-colors"
                      >
                        <Clock className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => removeTask(task.id)}
                        className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
              
              <div className="flex justify-end mt-6">
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveTaskTemplate}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1"
                  >
                    <Save className="w-5 h-5" />
                    Save
                  </button>
                </div>
              </div>
            </>
          ) : (
            <Reorder.Group axis="y" values={userTasks} onReorder={setUserTasks} className="space-y-3">
              {userTasks.map((task) => {
                const isDone = completed.includes(task.id);
                const isPast = isPastTask(task);
                
                return (
                  <Reorder.Item 
                    key={task.id}
                    value={task}
                    drag
                    className={`relative p-4 rounded-xl transition-all ${
                      isDone
                        ? "bg-green-50 border border-green-100"
                        : isPast
                          ? "bg-gray-50 border border-gray-100 opacity-75"
                          : "bg-white border border-gray-100 hover:border-indigo-100"
                    } shadow-sm hover:shadow-md`}
                    whileDrag={{
                      scale: 1.03,
                      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                      zIndex: 10,
                      cursor: "grabbing"
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <div 
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => toggleTask(task.id, task.text)}
                      >
                        {isDone ? (
                          <CheckCircle className="w-6 h-6 text-green-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-300" />
                        )}
                      </div>
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => toggleTask(task.id, task.text)}
                      >
                        <p className={`text-md font-medium ${
                          isDone 
                            ? "text-gray-500 line-through" 
                            : isPast
                              ? "text-gray-500"
                              : "text-gray-700"
                        }`}>
                          {task.text}
                        </p>
                        {task.scheduledTime && (
                          <p className={`text-xs mt-1 flex items-center ${
                            isDone 
                              ? "text-green-500"
                              : isPast
                                ? "text-gray-400"
                                : "text-indigo-600"
                          }`}>
                            <Clock className="w-3 h-3 mr-1" />
                            {formatTimeDisplay(task.scheduledTime)}
                            {task.completedAt && isDone && (
                              <span className="ml-2">✓ Done at {task.completedAt}</span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="cursor-grab touch-none">
                        <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="2" cy="2" r="2" fill="#D1D5DB"/>
                          <circle cx="2" cy="10" r="2" fill="#D1D5DB"/>
                          <circle cx="2" cy="18" r="2" fill="#D1D5DB"/>
                          <circle cx="10" cy="2" r="2" fill="#D1D5DB"/>
                          <circle cx="10" cy="10" r="2" fill="#D1D5DB"/>
                          <circle cx="10" cy="18" r="2" fill="#D1D5DB"/>
                        </svg>
                      </div>
                    </div>
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          )}
        </div>

        {/* Progress Indicator */}
        {!isLoading && !isEditing && (
          <div className="px-4 pb-4">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-indigo-700">
                Today's Progress
              </span>
              <span className="text-sm font-medium text-gray-500">
                {completed.length} of {userTasks.length} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <motion.div
                className="bg-indigo-600 h-2.5 rounded-full"
                initial={{ width: 0 }}
                animate={{
                  width: `${userTasks.length ? (completed.length / userTasks.length) * 100 : 0}%`,
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              ></motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}